import { dayjsUtc } from 'lib/dayjs'
import { FormikContextType } from 'formik'
import { LookupAddress } from 'lib/models/address'
import get from 'lodash/get'
import identity from 'lodash/identity'

interface GetFieldStateProps<T> {
  formik: Partial<FormikContextType<T>>
  name: string
}

export const getFieldState = <T>({ formik, name }: GetFieldStateProps<T>) => {
  const { errors, touched, status, values } = formik

  const hasError = !!get(errors, name) && !!get(touched, name)
  const value = get(values, name)
  const hasValue = value?.toString()?.length
  const hasHighlight = status?.highlightEmpty && !hasValue

  return { hasError, hasValue, hasHighlight }
}

export type DateParts = { day: string; month: string; year: string }

// parsed in UTC so no local timezone can shift the day
export const splitIsoDate = (isoDate: string | null): DateParts => {
  if (!isoDate) return { day: '', month: '', year: '' }

  const date = dayjsUtc(isoDate)
  return {
    day: String(date.date()),
    month: String(date.month() + 1),
    year: String(date.year()),
  }
}

export const composeIsoDate = ({
  day,
  month,
  year,
}: DateParts): string | null => {
  if (!day || !month || !year) return null
  if (year.length !== 4) return null

  const date = dayjsUtc(
    `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`,
    'YYYY-MM-DD',
    true
  )

  return date.isValid() ? date.format('YYYY-MM-DD') : null
}

export const formatLoqateAddress = (address: LookupAddress) => {
  if (address.type !== 'Commercial' || !address.company) {
    return address
  }

  return {
    ...address,
    lineOne: address.company,
    lineTwo: [address.lineOne, address.lineTwo].filter(identity).join(', '),
  }
}
